import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Context for the global NewMessageComposer panel.
 *
 * Shared across App, HomeNavBar, and MessagePage so the floating composer
 * can be opened from the navbar pencil icon and persist across routes.
 */

const ComposerContext = createContext(null);

export function NewMessageComposerProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('search'); // 'search' | 'compose'
  const [recipient, setRecipient] = useState(null);
  const [draftText, setDraftText] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const openComposer = useCallback(() => {
    if (isOpen) {
      // Already open — just expand and focus
      setIsExpanded(true);
      return;
    }
    setIsOpen(true);
    setMode('search');
    setRecipient(null);
    setDraftText('');
    setIsExpanded(true);
  }, [isOpen]);

  const closeComposer = useCallback(() => {
    setIsOpen(false);
    setMode('search');
    setRecipient(null);
    setDraftText('');
    setIsExpanded(true);
  }, []);

  const selectRecipient = useCallback((user) => {
    setRecipient(user);
    setMode('compose');
    setDraftText('');
    setIsExpanded(true);
  }, []);

  const backToSearch = useCallback(() => {
    setMode('search');
    setRecipient(null);
    setDraftText('');
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const value = {
    isOpen,
    mode,
    recipient,
    draftText,
    isExpanded,
    openComposer,
    closeComposer,
    selectRecipient,
    backToSearch,
    setDraftText,
    toggleExpanded,
  };

  return (
    <ComposerContext.Provider value={value}>
      {children}
    </ComposerContext.Provider>
  );
}

export function useComposer() {
  const ctx = useContext(ComposerContext);
  if (!ctx) throw new Error('useComposer must be used within NewMessageComposerProvider');
  return ctx;
}
